import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { newsletterApi } from '../../services/api';
import '../../styles/Home.css';

export default function Footer() {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await newsletterApi.subscribe({ email: email.trim() });
            if (response.success) {
                setSubscribed(true);
                setEmail('');
            } else {
                setError(response.error || t('errors.subscriptionFailed'));
            }
        } catch {
            setError(t('errors.connectionFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const quickLinks = [
        { name: 'Forms of Bhairava', href: '/bhairav/forms' },
        { name: 'Bhairava Tattva', href: '/bhairav/tattva' },
        { name: 'Sacred Stotras', href: '/bhairav/stotras' },
        { name: 'Shastra Stories', href: '/bhairav/shastra-stories' },
        { name: 'Life Purpose Guide', href: '/bhairav/life-purpose' },
        { name: 'Naam Japa', href: '/bhairav/naam-japa' },
    ];

    const exploreLinks = [
        { name: 'Shiv Section', href: '/shiv' },
        { name: 'Bhairava Quiz', href: '/quiz' },
        { name: 'Films', href: '/films' },
        { name: 'Resources', href: '/resources' },
        { name: 'Articles', href: '/bhairav/articles' },
        { name: 'Basic Vidhi', href: '/bhairav/vidhi' },
        { name: 'Pilgrimage Places', href: '/bhairav/pilgrimage-places' },
        { name: 'Sacred Books', href: '/bhairav/books' },
        { name: 'Divine Images', href: '/bhairav/images' },
    ];

    return (
        <footer className="relative bg-gradient-to-br from-cosmic via-indigo-900 to-purple-900 overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-divine/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-3xl" />
            </div>

            {/* ===== SANSKRIT SHLOKA SECTION ===== */}
            <div className="relative border-b border-yellow-400/20">
                <div className="max-w-5xl mx-auto px-6 py-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Sanskrit Shloka with Golden Animation */}
                        <p className="text-lg md:text-xl font-noto-devanagari golden-shine-text leading-relaxed mb-4">
                            करचरण कृतं वाक्कायजं कर्मजं वा ।<br />
                            श्रवणनयनजं वा मानसं वापराधं ।<br />
                            विहितमविहितं वा सर्वमेतत्क्षमस्व ।<br />
                            जय जय करुणाब्धे श्रीमहादेव शम्भो ॥
                        </p>

                        {/* English Translation */}
                        <p className="text-sm md:text-base text-white/80 font-noto-serif italic max-w-3xl mx-auto leading-relaxed">
                            O Compassionate Mahadeva! O Sambhu! Victory to Thee! Please do forgive all the errors
                            committed (by me) with the hands, legs, speech, body, through actions, through the ears,
                            eyes, mind or by (not doing) ordained actions or (by doing) prohibited actions.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* ===== MAIN FOOTER CONTENT ===== */}
            <div className="relative max-w-7xl mx-auto px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

                    {/* Brand Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="lg:col-span-1"
                    >
                        <Link to="/" className="inline-block mb-6">
                            <h3 className="text-2xl font-dancing golden-shine-text-bright">
                                {t('footer.brandName')}
                            </h3>
                            <p className="text-sm font-noto-devanagari" style={{ color: '#ffd700' }}>
                                {t('footer.brandNameHi')}
                            </p>
                        </Link>
                        <p className="text-white/70 font-noto-serif text-sm leading-relaxed mb-6">
                            {t('footer.description')}
                        </p>

                        {/* Social Links */}
                        <div className="flex items-center gap-4">
                            <a
                                href="https://instagram.com/bhairav.aaradhyaa"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/40 hover:border-yellow-400/60 flex items-center justify-center transition-all duration-300 group"
                                aria-label="Follow us on Instagram"
                            >
                                <svg className="w-5 h-5" style={{ color: '#ffd700' }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.326-2.633 1.301-3.608.975-.975 2.242-1.24 3.608-1.301 1.266-.058 1.646-.07 4.85-.07zm0-2.163c-3.259 0-3.667.014-4.947.072-1.627.073-3.355.409-4.64 1.694-1.285 1.285-1.621 3.013-1.694 4.64-.058 1.28-.072 1.688-.072 4.947s.014 3.667.072 4.947c.073 1.627.409 3.355 1.694 4.64 1.285 1.285 3.013 1.621 4.64 1.694 1.28.058 1.688.072 4.947.072s3.667-.014 4.947-.072c1.627-.073 3.355-.409 4.64-1.694 1.285-1.285 1.621-3.013 1.694-4.64.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.073-1.627-.409-3.355-1.694-4.64-1.285-1.285-3.013-1.621-4.64-1.694-1.28-.058-1.688-.072-4.947-.072z" />
                                    <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 11-2.88 0 1.44 1.44 0 012.88 0z" />
                                </svg>
                            </a>
                            <a
                                href="https://www.facebook.com/share/1CcgtqTSjm/?mibextid=wwXIfr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/40 hover:border-yellow-400/60 flex items-center justify-center transition-all duration-300 group"
                                aria-label="Follow us on Facebook"
                            >
                                <svg className="w-5 h-5" style={{ color: '#ffd700' }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                            </a>
                            <a
                                href="https://www.youtube.com/@Bhairavaaradhyaa"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/40 hover:border-yellow-400/60 flex items-center justify-center transition-all duration-300 group"
                                aria-label="Watch us on YouTube"
                            >
                                <svg className="w-5 h-5" style={{ color: '#ffd700' }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </motion.div>

                    {/* Quick Links */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        <h4 className="text-lg font-poppins font-bold mb-6" style={{ color: '#ffd700' }}>
                            {t('footer.quickLinks')}
                        </h4>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.href}
                                        className="text-white/70 hover:text-yellow-400 font-noto-serif text-sm transition-colors duration-300 flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400/50 group-hover:bg-yellow-400 transition-colors" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Explore More */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <h4 className="text-lg font-poppins font-bold mb-6" style={{ color: '#ffd700' }}>
                            {t('footer.exploreMore')}
                        </h4>
                        <ul className="space-y-3">
                            {exploreLinks.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.href}
                                        className="text-white/70 hover:text-yellow-400 font-noto-serif text-sm transition-colors duration-300 flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400/50 group-hover:bg-yellow-400 transition-colors" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Newsletter & Contact */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <h4 className="text-lg font-poppins font-bold mb-6" style={{ color: '#ffd700' }}>
                            {t('footer.stayConnected')}
                        </h4>

                        {/* Newsletter Form */}
                        <form onSubmit={handleSubscribe} className="mb-6">
                            <p className="text-white/70 font-noto-serif text-sm mb-4">
                                {t('footer.communityDesc')}
                            </p>
                            {!subscribed ? (
                                <div className="flex flex-col gap-3">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setError(null); }}
                                        placeholder={t('footer.emailPlaceholder') as string}
                                        className="w-full px-4 py-3 rounded-lg bg-white/10 border border-yellow-400/30 text-white placeholder-white/50 font-noto-serif text-sm focus:outline-none focus:border-yellow-400/60 transition-colors"
                                        required
                                        disabled={isSubmitting}
                                    />
                                    {error && (
                                        <p className="text-red-400 font-noto-serif text-xs">{error}</p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="premium-btn font-poppins text-sm py-3 disabled:opacity-70"
                                    >
                                        {isSubmitting ? t('footer.subscribing') : t('footer.subscribe')}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-yellow-400 font-noto-serif text-sm">
                                    {t('success.subscribed')} 🙏
                                </p>
                            )}
                        </form>

                        {/* Contact & Donate Links */}
                        <div className="flex flex-col gap-3">
                            <Link
                                to="/contact"
                                className="text-white/70 hover:text-yellow-400 font-noto-serif text-sm transition-colors duration-300 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {t('footer.contactUs')}
                            </Link>
                            <Link
                                to="/support"
                                className="text-white/70 hover:text-yellow-400 font-noto-serif text-sm transition-colors duration-300 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                {t('footer.supportMission')}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* ===== BOTTOM BAR ===== */}
            <div className="relative border-t border-yellow-400/20">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-white/60 font-noto-serif text-sm text-center md:text-left">
                            &copy; {new Date().getFullYear()} {t('footer.allRightsReserved')}
                        </p>
                        <div className="flex items-center gap-4 text-white/40 font-noto-serif text-xs">
                            <Link to="/terms" className="hover:text-yellow-400 transition-colors">{t('footer.termsOfService')}</Link>
                            <span>|</span>
                            <Link to="/privacy" className="hover:text-yellow-400 transition-colors">{t('footer.privacyPolicy')}</Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Glow Line */}
            <motion.div
                className="h-1 bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent"
                animate={{ opacity: [0.4, 0.8, 0.4] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
        </footer>
    );
}